import {PortalEntry} from '../../types-signiant';

/**
 * One line of the lazy portal tree, held as a **flat list**: expanding is
 * "splice my children in after me", collapsing is "drop the following rows
 * deeper than me". Both are plain array operations, so there is no recursive
 * rendering and no per-node signal.
 */
export interface TreeRow {
  entry: PortalEntry;
  depth: number;
  expanded: boolean;
  loading: boolean;
  /** Set when expanding failed, so the row can say so instead of doing nothing. */
  error?: string;
  /** Expanded and genuinely empty — otherwise an open chevron with no children
   *  underneath reads as a click that did nothing. */
  empty?: boolean;
}

/**
 * A row's identity.
 *
 * Rows are addressed by this key and never by object identity. They are
 * immutable: every state change produces a new object so the signal sees a
 * change, which means a reference captured before an update is stale by the time
 * an async callback runs. Looking a row up by that stale reference finds
 * nothing, and the update then silently does nothing — which is exactly how
 * expanding a folder left a spinner turning forever.
 *
 * `folderId` is part of the key because a portal can expose several member
 * folders and the same path may occur in more than one.
 */
export function rowKey(row: TreeRow | PortalEntry): string {
  const entry = 'entry' in row ? row.entry : row;
  return entry.folderId + '|' + entry.path;
}

function indexOfKey(rows: readonly TreeRow[], key: string): number {
  return rows.findIndex(r => rowKey(r) === key);
}

export function newRow(entry: PortalEntry, depth: number): TreeRow {
  return {entry, depth, expanded: false, loading: false};
}

/** Replace one row, addressed by key. Unknown key leaves the list unchanged. */
export function patchRow(rows: readonly TreeRow[], key: string,
                         changes: Partial<TreeRow>): TreeRow[] {
  const at = indexOfKey(rows, key);
  if (at < 0) {
    return rows as TreeRow[];
  }
  const next = [...rows];
  next[at] = {...next[at], ...changes};
  return next;
}

/** Insert a folder's children directly beneath it and mark it expanded. */
export function expandRow(rows: readonly TreeRow[], key: string,
                          entries: readonly PortalEntry[]): TreeRow[] {
  const at = indexOfKey(rows, key);
  if (at < 0) {
    return rows as TreeRow[];
  }
  const parent = rows[at];
  const children = entries.map(e => newRow(e, parent.depth + 1));
  return [
    ...rows.slice(0, at),
    {...parent, expanded: true, loading: false, error: undefined,
      empty: children.length === 0},
    ...children,
    ...rows.slice(at + 1),
  ];
}

/**
 * Drop everything below a folder and mark it collapsed. Removes *all* deeper
 * rows, however many levels were opened underneath it.
 */
export function collapseRow(rows: readonly TreeRow[], key: string): TreeRow[] {
  const at = indexOfKey(rows, key);
  if (at < 0) {
    return rows as TreeRow[];
  }
  let end = at + 1;
  while (end < rows.length && rows[end].depth > rows[at].depth) {
    end++;
  }
  return [
    ...rows.slice(0, at),
    {...rows[at], expanded: false, empty: undefined},
    ...rows.slice(end),
  ];
}

/** The current row for a key, or undefined when it is gone. */
export function findRow(rows: readonly TreeRow[], key: string): TreeRow | undefined {
  const at = indexOfKey(rows, key);
  return at < 0 ? undefined : rows[at];
}
